import React from 'react';
import { format } from 'date-fns';
import MetricCard from './MetricCard';

import TrainingLoadACWRChart from './TrainingLoadACWRChart';
import TrainingLoadWellnessChart from './TrainingLoadWellnessChart';
import ReadinessChart from './ReadinessChart';
import WellnessHistoryTable from './WellnessHistoryTable';
import { calculateLoadRatioTrend } from '../../../utils/trainingLoadCalculations';

const ReportLayout = ({ 
  reportConfig, 
  summaryMetrics, 
  chartData30, 
  chartData90,
  chartDataRange,
  sessionHistory,
  wellnessHistory,
  logoSrc,
  allSessions // NEW: Need all sessions for trend calculations
}) => {
  const { reportDate, includeIdentity, name, email, includeSessionHistory, includeWellnessHistory } = reportConfig;

  const formatReportDate = (dateStr) => {
    return format(new Date(dateStr), 'dd MMM yyyy');
  };

  const formatGeneratedDate = () => {
    return format(new Date(), 'dd MMM yyyy HH:mm');
  };

  // Calculate trend data for ACWR card using the same function as dashboard
  const loadRatioTrendData = allSessions ? calculateLoadRatioTrend(allSessions) : null;

  // Prepare wellness data for the wellness card (matching dashboard format)
  const wellnessCardData = summaryMetrics?.wellness ? {
    sleep: summaryMetrics?.wellness?.sleep,
    fatigue: summaryMetrics?.wellness?.fatigue,
    soreness: summaryMetrics?.wellness?.soreness,
    stress: summaryMetrics?.wellness?.stress,
    hooperIndex: summaryMetrics?.wellness?.hooperIndex,
    qualitativeRating: summaryMetrics?.wellness?.qualitativeRating,
    qualitativeGuidance: summaryMetrics?.wellness?.qualitativeGuidance,
    dailyChange: summaryMetrics?.wellness?.dailyChange
  } : null;

  // Common header component for reuse
  const ReportHeader = () => (
    <div style={{ marginBottom: '20px', borderBottom: '2px solid #E2E8F0', paddingBottom: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <img 
            src={logoSrc} 
            alt="Hub Logo" 
            style={{ height: '40px', marginBottom: '8px' }}
          />
        </div>
        <div style={{ textAlign: 'right' }}>
          <h1 style={{ fontSize: '20px', fontWeight: '600', margin: '0 0 8px 0' }}>
            Training Load Report
          </h1>
          <p style={{ fontSize: '11px', color: '#64748B', margin: '2px 0' }}>
            Report date: {formatReportDate(reportDate)}
          </p>
          <p style={{ fontSize: '11px', color: '#64748B', margin: '2px 0' }}>
            Generated: {formatGeneratedDate()}
          </p>
          {includeIdentity && (name || email) && (
            <div style={{ marginTop: '8px', fontSize: '11px', color: '#64748B' }}>
              {name && <p style={{ margin: '2px 0' }}>{name}</p>}
              {email && <p style={{ margin: '2px 0' }}>{email}</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
      {/* PAGE 1: Header + Today's Summary (vertical stack) */}
      <div 
        id="pdf-summary-section"
        style={{
          width: '794px',
          backgroundColor: '#ffffff',
          fontFamily: 'Inter, sans-serif',
          color: '#1E293B',
          padding: '20mm',
          breakInside: 'avoid',
          pageBreakInside: 'avoid'
        }}
      >
        <ReportHeader />
        
        <h2 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>Today's Summary</h2>
        
        {/* Vertical stacked cards - reusing exact dashboard components */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12mm' }}>
          {/* Training Load (ACWR) Card */}
          {summaryMetrics?.acwr && (
            <div style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
              <MetricCard
                title="Load Ratio (ACWR)"
                value={summaryMetrics?.acwr?.value?.toFixed(2) || '—'}
                icon="Target"
                description={summaryMetrics?.acwr?.riskBand?.label || "Acute:Chronic ratio"}
                loadRiskCategory={summaryMetrics?.acwr?.riskBand?.category || null}
                loadRatioQualitativeRating={summaryMetrics?.acwr?.qualitativeRating}
                todayAULoad={summaryMetrics?.acwr?.todayAULoad}
                yesterdayAULoad={summaryMetrics?.acwr?.yesterdayAULoad}
                dailyChangePercent={loadRatioTrendData?.dailyChangePercent || null}
                dailyChange={loadRatioTrendData?.dailyChange || null}
                sevenDayTrend={loadRatioTrendData?.sevenDayTrend || null}
              />
            </div>
          )}

          {/* Wellness Score Card */}
          {wellnessCardData && (
            <div style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
              <MetricCard
                title="Wellness Score"
                value={wellnessCardData?.hooperIndex || 'No Data'}
                unit={wellnessCardData?.hooperIndex ? '/28' : ''}
                icon="Heart"
                description="Total wellness score today"
                wellnessBreakdown={wellnessCardData}
                hasWellnessData={true}
                wellnessQualitativeRating={wellnessCardData?.qualitativeRating}
                wellnessQualitativeGuidance={wellnessCardData?.qualitativeGuidance}
                dailyChange={wellnessCardData?.dailyChange}
              />
            </div>
          )}

          {/* Readiness Index Card */}
          {summaryMetrics?.readiness && (
            <div style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
              <MetricCard
                title="Readiness Index"
                value={summaryMetrics?.readiness?.value?.toFixed(2) || '—'}
                icon="Activity"
                description={summaryMetrics?.readiness?.band === 'green' ? 'Good to go' :
                            summaryMetrics?.readiness?.band === 'amber' ? 'Proceed with caution' :
                            summaryMetrics?.readiness?.band === 'red' ? 'High strain/risk' : 'Insufficient data'}
                readinessData={summaryMetrics?.readiness}
                isLoadingReadiness={false}
              />
            </div>
          )}
        </div>
      </div>

      {/* PAGE 2: Figure 1 - Training Load and ACWR (30 days) */}
      <div 
        id="pdf-figure-1"
        style={{
          width: '794px',
          backgroundColor: '#ffffff',
          fontFamily: 'Inter, sans-serif',
          color: '#1E293B',
          padding: '20mm',
          breakInside: 'avoid',
          pageBreakInside: 'avoid'
        }}
      >
        <ReportHeader />
        <h2 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>Figures</h2>
        <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>Training Load and ACWR (30 days)</h3>
        <div style={{ 
          width: '100%', 
          maxWidth: '710px', 
          border: '1px solid #E2E8F0', 
          borderRadius: '8px', 
          padding: '16px', 
          backgroundColor: '#FFFFFF',
          breakInside: 'avoid',
          pageBreakInside: 'avoid'
        }}>
          <TrainingLoadACWRChart data={chartData30} width={678} height={300} title="Training Load and ACWR (30 days)" />
        </div>
        <p style={{ fontSize: '10px', color: '#64748B', marginTop: '8px', textAlign: 'center' }}>
          Training load and ACWR, last 30 days (ending {formatReportDate(reportDate)})
        </p>
      </div>

      {/* PAGE 3: Figure 2 - Training Load and ACWR (90 days) */}
      <div 
        id="pdf-figure-2"
        style={{
          width: '794px',
          backgroundColor: '#ffffff',
          fontFamily: 'Inter, sans-serif',
          color: '#1E293B',
          padding: '20mm',
          breakInside: 'avoid',
          pageBreakInside: 'avoid'
        }}
      >
        <ReportHeader />
        <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>Training Load and ACWR (90 days)</h3>
        <div style={{ 
          width: '100%', 
          maxWidth: '710px', 
          border: '1px solid #E2E8F0', 
          borderRadius: '8px', 
          padding: '16px', 
          backgroundColor: '#FFFFFF',
          breakInside: 'avoid',
          pageBreakInside: 'avoid'
        }}>
          <TrainingLoadACWRChart data={chartData90} width={678} height={300} title="Training Load and ACWR (90 days)" />
        </div>
        <p style={{ fontSize: '10px', color: '#64748B', marginTop: '8px', textAlign: 'center' }}>
          Training load and ACWR, last 90 days (ending {formatReportDate(reportDate)})
        </p>
      </div>

      {/* PAGE 4: Figure 3 - Training Load and Wellness (selected range) */}
      <div 
        id="pdf-figure-3"
        style={{
          width: '794px',
          backgroundColor: '#ffffff',
          fontFamily: 'Inter, sans-serif',
          color: '#1E293B',
          padding: '20mm',
          breakInside: 'avoid',
          pageBreakInside: 'avoid'
        }}
      >
        <ReportHeader />
        <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>Training Load and Wellness</h3>
        <div style={{ 
          width: '100%', 
          maxWidth: '710px', 
          border: '1px solid #E2E8F0', 
          borderRadius: '8px', 
          padding: '16px', 
          backgroundColor: '#FFFFFF',
          breakInside: 'avoid',
          pageBreakInside: 'avoid'
        }}>
          <TrainingLoadWellnessChart data={chartDataRange} width={678} height={300} />
        </div>
        <p style={{ fontSize: '10px', color: '#64748B', marginTop: '8px', textAlign: 'center' }}>
          Training load and wellness (Hooper Index), selected date range
        </p>
      </div>

      {/* PAGE 5: Figure 4 - Readiness (selected range) */}
      <div 
        id="pdf-figure-4"
        style={{
          width: '794px',
          backgroundColor: '#ffffff',
          fontFamily: 'Inter, sans-serif',
          color: '#1E293B',
          padding: '20mm',
          breakInside: 'avoid',
          pageBreakInside: 'avoid'
        }}
      >
        <ReportHeader />
        <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>Readiness Index</h3>
        <div style={{ 
          width: '100%', 
          maxWidth: '710px', 
          border: '1px solid #E2E8F0', 
          borderRadius: '8px', 
          padding: '16px', 
          backgroundColor: '#FFFFFF',
          breakInside: 'avoid',
          pageBreakInside: 'avoid'
        }}>
          <ReadinessChart data={chartDataRange} width={678} height={300} />
        </div>
        <p style={{ fontSize: '10px', color: '#64748B', marginTop: '8px', textAlign: 'center' }}>
          Readiness index, selected date range
        </p>
      </div>

      {/* PAGES 6+: Tables */}
      {(includeSessionHistory || includeWellnessHistory) && (
        <div 
          id="pdf-tables-section"
          style={{
            width: '794px',
            backgroundColor: '#ffffff',
            fontFamily: 'Inter, sans-serif',
            color: '#1E293B',
            padding: '20mm'
          }}
        >
          <ReportHeader />
          <h2 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>Tables</h2>
          
          {/* Session History Table */}
          {includeSessionHistory && (
            <div style={{ marginBottom: '32px' }}>
              <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>Training Load Session History</h3>
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse',
                fontSize: '10px'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#F1F5F9', borderBottom: '2px solid #E2E8F0' }}>
                    <th style={{ padding: '8px', textAlign: 'left', fontWeight: '600' }}>Date</th>
                    <th style={{ padding: '8px', textAlign: 'left', fontWeight: '600' }}>Activity</th>
                    <th style={{ padding: '8px', textAlign: 'right', fontWeight: '600' }}>Duration</th>
                    <th style={{ padding: '8px', textAlign: 'right', fontWeight: '600' }}>RPE</th>
                    <th style={{ padding: '8px', textAlign: 'right', fontWeight: '600' }}>Load</th>
                    <th style={{ padding: '8px', textAlign: 'center', fontWeight: '600' }}>ACWR</th>
                    <th style={{ padding: '8px', textAlign: 'center', fontWeight: '600' }}>Wellness</th>
                    <th style={{ padding: '8px', textAlign: 'center', fontWeight: '600' }}>Readiness</th>
                  </tr>
                </thead>
                <tbody>
                  {sessionHistory?.map((session, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid #E2E8F0' }}>
                      <td style={{ padding: '8px' }}>{format(new Date(session.date), 'dd/MM/yyyy')}</td>
                      <td style={{ padding: '8px' }}>{session?.activityType || '—'}</td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>{session?.minutes}m</td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>{session?.rpe}</td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>{session?.load}</td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>
                        {session?.acwr ? session?.acwr?.toFixed(2) : '—'}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>
                        {session?.wellness ? `${session?.wellness}/28` : '—'}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>
                        {session?.readiness ? session?.readiness?.toFixed(2) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Wellness History Table */}
          {includeWellnessHistory && (
            <div style={{ marginBottom: '32px' }}>
              <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>Wellness Score History</h3>
              <WellnessHistoryTable wellnessHistory={wellnessHistory} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ReportLayout;